import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Save, Plus, Trash2, FileText, Users
} from 'lucide-react';
import api from '../services/api';
import './DocumentCreate.css';

function DocumentCreate() {
  const { id } = useParams(); // Если есть id — режим редактирования
  const navigate = useNavigate();
  const { t } = useTranslation(['documents', 'common']);
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Справочники
  const [docTypes, setDocTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Форма документа
  const [form, setForm] = useState({
    registration_number: '',
    document_type: '',
    status: '',
    authority: '',
    priority: '',
    title: '',
    description: '',
    incoming_number: '',
    incoming_date: '',
    deadline: '',
    responsible_department: '',
  });

  // Пункты документа
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchReferences();
    if (isEdit) {
      fetchDocument();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReferences = async () => {
    try {
      const [typesRes, statusesRes, authRes, prioRes, deptRes] = await Promise.all([
        api.getDocumentTypes(),
        api.getDocumentStatuses(),
        api.getAuthorities(),
        api.getPriorities(),
        api.getDepartments(),
      ]);
      setDocTypes(typesRes.data.results || typesRes.data);
      const sts = statusesRes.data.results || statusesRes.data;
      setStatuses(sts);
      setAuthorities(authRes.data.results || authRes.data);
      setPriorities(prioRes.data.results || prioRes.data);
      setDepartments(deptRes.data.results || deptRes.data);

      // Если создание — установить статус по умолчанию (registered)
      if (!isEdit && sts.length > 0) {
        const registered = sts.find(s => s.code === 'registered');
        if (registered) {
          setForm(prev => ({ ...prev, status: String(registered.id) }));
        }
      }
    } catch (err) {
      console.error('Ошибка загрузки справочников:', err);
    }
  };

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const res = await api.getDocument(id);
      const doc = res.data;

      setForm({
        registration_number: doc.registration_number || '',
        document_type: doc.document_type ? String(doc.document_type) : '',
        status: doc.status ? String(doc.status) : '',
        authority: doc.authority ? String(doc.authority) : '',
        priority: doc.priority ? String(doc.priority) : '',
        title: doc.title || '',
        description: doc.description || '',
        incoming_number: doc.incoming_number || '',
        incoming_date: doc.incoming_date || '',
        deadline: doc.deadline || '',
        responsible_department: doc.responsible_department ? String(doc.responsible_department) : '',
      });

      if (doc.items && doc.items.length > 0) {
        setItems(doc.items.map(item => ({
          id: item.id,
          item_number: item.item_number,
          text: item.text,
          deadline: item.deadline || '',
          status: item.status ? String(item.status) : '',
          executors: (item.executors || []).map(ex => ({
            department: ex.department ? String(ex.department) : '',
            is_responsible: ex.is_responsible || false,
          })),
        })));
      }

      setLoading(false);
    } catch (err) {
      console.error('Ошибка загрузки документа:', err);
      setLoading(false);
      alert(t('documents:messages.loadDocError'));
      navigate(-1);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Пункты
  const addItem = () => {
    setItems(prev => [...prev, {
      item_number: String(prev.length + 1),
      text: '',
      deadline: '',
      status: form.status, // наследуем статус документа
      executors: [],
    }]);
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Исполнители пункта
  const addExecutor = (itemIndex) => {
    setItems(prev => prev.map((item, i) =>
      i === itemIndex
        ? { ...item, executors: [...item.executors, { department: '', is_responsible: false }] }
        : item
    ));
  };

  const removeExecutor = (itemIndex, execIndex) => {
    setItems(prev => prev.map((item, i) =>
      i === itemIndex
        ? { ...item, executors: item.executors.filter((_, j) => j !== execIndex) }
        : item
    ));
  };

  const updateExecutor = (itemIndex, execIndex, field, value) => {
    setItems(prev => prev.map((item, i) =>
      i === itemIndex
        ? {
          ...item,
          executors: item.executors.map((ex, j) =>
            j === execIndex ? { ...ex, [field]: value } : ex
          )
        }
        : item
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.registration_number || !form.title || !form.document_type || !form.status || !form.responsible_department) {
      alert(t('documents:form.requiredFieldsAlert'));
      return;
    }

    try {
      setSaving(true);

      const data = {
        registration_number: form.registration_number,
        title: form.title,
        description: form.description,
        document_type: parseInt(form.document_type),
        status: parseInt(form.status),
        authority: form.authority ? parseInt(form.authority) : null,
        priority: form.priority ? parseInt(form.priority) : null,
        incoming_number: form.incoming_number,
        incoming_date: form.incoming_date || null,
        deadline: form.deadline || null,
        responsible_department: parseInt(form.responsible_department),
        items: items.map(item => ({
          item_number: item.item_number,
          text: item.text,
          deadline: item.deadline || null,
          status: parseInt(item.status || form.status),
          executors: item.executors
            .filter(ex => ex.department)
            .map(ex => ({
              department: parseInt(ex.department),
              is_responsible: ex.is_responsible,
            })),
        })),
      };

      if (isEdit) {
        await api.updateDocument(id, data);
        alert(t('documents:form.documentUpdated'));
      } else {
        await api.createDocument(data);
        alert(t('documents:form.documentCreated'));
      }

      navigate('/documents');
    } catch (err) {
      console.error('Ошибка сохранения:', err);
      const detail = err.response?.data?.detail
        || err.response?.data?.registration_number?.[0]
        || JSON.stringify(err.response?.data)
        || err.message;
      alert('Ошибка: ' + detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="doc-create-page">
        <div className="loading-spinner">{t('documents:misc.loadingDocument')}</div>
      </div>
    );
  }

  return (
    <div className="doc-create-page">
      {/* Шапка */}
      <div className="doc-create-header">
        <div className="doc-create-header-left">
          <h1>{isEdit ? t('documents:pageTitles.editDocument') : t('documents:pageTitles.createDocument')}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Основная информация */}
        <div className="form-card">
          <h2><FileText size={18} /> {t('documents:form.mainInfo')}</h2>

          <div className="form-row">
            <div className="form-group">
              <label>{t('documents:form.registrationNumberRequired')}</label>
              <input
                type="text"
                className="form-control"
                placeholder={t('documents:form.registrationNumberPlaceholder')}
                value={form.registration_number}
                onChange={e => handleFormChange('registration_number', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('documents:form.typeRequired')}</label>
              <select
                className="form-control"
                value={form.document_type}
                onChange={e => handleFormChange('document_type', e.target.value)}
                required
              >
                <option value="">{t('documents:form.selectType')}</option>
                {docTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>{t('documents:form.titleRequired')}</label>
            <input
              type="text"
              className="form-control"
              placeholder={t('documents:form.titlePlaceholder')}
              value={form.title}
              onChange={e => handleFormChange('title', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('documents:form.content')}</label>
            <textarea
              className="form-control"
              rows="4"
              placeholder={t('documents:form.descriptionPlaceholder')}
              value={form.description}
              onChange={e => handleFormChange('description', e.target.value)}
            />
          </div>

          <div className="form-row form-row-4">
            <div className="form-group">
              <label>{t('documents:form.source')}</label>
              <select
                className="form-control"
                value={form.authority}
                onChange={e => handleFormChange('authority', e.target.value)}
              >
                <option value="">{t('documents:form.sourceNotSet')}</option>
                {authorities.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('documents:form.priority')}</label>
              <select
                className="form-control"
                value={form.priority}
                onChange={e => handleFormChange('priority', e.target.value)}
              >
                <option value="">{t('documents:form.priorityNotSet')}</option>
                {priorities.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('documents:form.statusRequired')}</label>
              <select
                className="form-control"
                value={form.status}
                onChange={e => handleFormChange('status', e.target.value)}
                required
              >
                <option value="">{t('documents:form.selectStatus')}</option>
                {statuses.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('documents:form.responsibleDeptRequired')}</label>
              <select
                className="form-control"
                value={form.responsible_department}
                onChange={e => handleFormChange('responsible_department', e.target.value)}
                required
              >
                <option value="">{t('documents:form.selectDepartment')}</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name_ru}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('documents:form.incomingNumberLabel')}</label>
              <input
                type="text"
                className="form-control"
                placeholder={t('documents:form.incomingNumberPlaceholder')}
                value={form.incoming_number}
                onChange={e => handleFormChange('incoming_number', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{t('documents:form.incomingDateLabel')}</label>
              <input
                type="date"
                className="form-control"
                value={form.incoming_date}
                onChange={e => handleFormChange('incoming_date', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{t('documents:form.generalDeadline')}</label>
              <input
                type="date"
                className="form-control"
                value={form.deadline}
                onChange={e => handleFormChange('deadline', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Пункты документа */}
        <div className="form-card">
          <div className="card-header-row">
            <h2><FileText size={18} /> {t('documents:items.title')} ({items.length})</h2>
            <button type="button" className="btn-add" onClick={addItem}>
              <Plus size={16} /> {t('documents:items.addItem')}
            </button>
          </div>

          {items.length === 0 ? (
            <p className="empty-hint">{t('documents:form.noItemsHint')}</p>
          ) : (
            <div className="items-form-list">
              {items.map((item, idx) => (
                <div key={idx} className="item-form-card">
                  <div className="item-form-header">
                    <h3>{t('documents:form.itemTitle', { number: item.item_number })}</h3>
                    <button type="button" className="btn-remove" onClick={() => removeItem(idx)}>
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="form-row">
                    <div className="form-group" style={{ maxWidth: '120px' }}>
                      <label>{t('common:table.number')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.item_number}
                        onChange={e => updateItem(idx, 'item_number', e.target.value)}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('documents:items.deadline')}</label>
                      <input
                        type="date"
                        className="form-control"
                        value={item.deadline}
                        onChange={e => updateItem(idx, 'deadline', e.target.value)}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>{t('common:table.status')}</label>
                      <select
                        className="form-control"
                        value={item.status}
                        onChange={e => updateItem(idx, 'status', e.target.value)}
                      >
                        <option value="">{t('documents:form.asDocument')}</option>
                        {statuses.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t('documents:form.itemOrderText')}</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder={t('documents:form.itemOrderPlaceholder')}
                      value={item.text}
                      onChange={e => updateItem(idx, 'text', e.target.value)}
                    />
                  </div>

                  {/* Исполнители пункта */}
                  <div className="executors-form-section">
                    <div className="executors-form-header">
                      <h4><Users size={14} /> {t('documents:executors.title')}</h4>
                      <button type="button" className="btn-add-small" onClick={() => addExecutor(idx)}>
                        <Plus size={14} /> {t('documents:executors.addExecutor')}
                      </button>
                    </div>

                    {item.executors.map((exec, exIdx) => (
                      <div key={exIdx} className="executor-form-row">
                        <select
                          className="form-control"
                          value={exec.department}
                          onChange={e => updateExecutor(idx, exIdx, 'department', e.target.value)}
                        >
                          <option value="">{t('documents:form.selectDepartment')}</option>
                          {departments.map(d => (
                            <option key={d.id} value={d.id}>{d.name_ru}</option>
                          ))}
                        </select>
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={exec.is_responsible}
                            onChange={e => updateExecutor(idx, exIdx, 'is_responsible', e.target.checked)}
                          />
                          {t('documents:executors.responsible')}
                        </label>
                        <button type="button" className="btn-remove-small" onClick={() => removeExecutor(idx, exIdx)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Кнопки сохранения */}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            {t('common:buttons.cancel')}
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            <Save size={16} />
            {saving ? t('common:table.loading') : (isEdit ? t('common:buttons.save') : t('documents:form.createDocument'))}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DocumentCreate;
